/**
 * Caprion Final Offer Display
 * 
 * Displays final loan offer details fetched from database.
 * Shows loan amount, deductions, disbursement, EMI, tenure, etc.
 * 
 * Component ID: LOS_CaprionFinalOffer_445
 * API: GET /api/caprion/final-offer-details/
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, IndianRupee, Calendar, Percent } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface CaprionFinalOfferProps {
  value?: any;
  onChange?: (data: any) => void;
  onDone?: () => void;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  isBuilderMode?: boolean;
}

interface FinalOfferData {
  loan_amount: number;
  tenure: number;
  tenure_unit: string;
  roi: number;
  repayment_frequency: string;
  processing_fee: number;
  gst_amount: number;
  total_deduction: number;
  net_disbursement: number;
  daily_installment: number;
  total_repayment: number;
  total_interest: number;
  lender_name: string;
  loan_code?: string;
  application_id?: string;
  status?: string;
}

const CaprionFinalOffer: React.FC<CaprionFinalOfferProps> = ({
  value,
  onChange,
  onDone,
  registerSubmit,
  isBuilderMode = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offerData, setOfferData] = useState<FinalOfferData | null>(null);

  useEffect(() => {
    if (!isBuilderMode) {
      fetchFinalOffer();
    }
  }, [isBuilderMode]);

  const fetchFinalOffer = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('[CaprionFinalOffer] Fetching final offer details from DB');
      
      const response = await axios.get(`${API_BASE_URL}/api/caprion/final-offer-details/`);
      
      if (response.data.success) {
        console.log('[CaprionFinalOffer] ✅ Offer details loaded:', response.data.data);
        setOfferData(response.data.data);
        
        // Notify parent component
        onChange?.({ finalOffer: response.data.data });
      } else {
        throw new Error(response.data.error || 'Failed to load offer details');
      }
    } catch (err: any) {
      console.error('[CaprionFinalOffer] Error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load offer details');
    } finally {
      setLoading(false);
    }
  };

  // Call update_lead API
  const callUpdateLead = async () => {
    try {
      console.log('[CaprionFinalOffer] Calling update_lead API');
      
      const response = await axios.post(`${API_BASE_URL}/api/caprion/update-lead-from-db/`);
      
      if (response.data.success) {
        console.log('[CaprionFinalOffer] ✅ update_lead successful');
        return true;
      } else {
        console.error('[CaprionFinalOffer] update_lead failed:', response.data.error);
        return false;
      }
    } catch (err: any) {
      console.error('[CaprionFinalOffer] update_lead error:', err);
      return false;
    }
  };

  // Register submit handler - calls update_lead before proceeding
  useEffect(() => {
    if (registerSubmit) {
      registerSubmit(async () => {
        console.log('[CaprionFinalOffer] Submit handler called - calling update_lead');
        const success = await callUpdateLead();
        return success;
      });
    }
  }, [registerSubmit]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (isBuilderMode) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-center text-intellend-green">Your Final Loan Offer</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-gray-500">Preview mode - Final offer will be displayed here</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-12 h-12 text-intellend-green animate-spin mb-4" />
          <p className="text-gray-600">Loading your final offer...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-6">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={fetchFinalOffer} className="w-full mt-4">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!offerData) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Final Offer Card */}
      <Card>
        <CardHeader className="bg-intellend-green text-white">
          <CardTitle className="text-center text-xl">Your Final Loan Offer</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {/* Loan Amount */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-base font-semibold text-gray-900">Loan Amount</span>
            <span className="text-lg font-bold text-gray-900">
              {formatCurrency(offerData.loan_amount)}
            </span>
          </div>

          {/* Deduction */}
          <div className="flex justify-between items-center py-3 border-b">
            <div>
              <span className="text-base font-semibold text-gray-900">Deduction</span>
              <p className="text-xs text-gray-500">ProcFee (incl. GST)</p>
            </div>
            <span className="text-lg font-bold text-gray-900">
              - {formatCurrency(offerData.total_deduction)}
            </span>
          </div>

          {/* Amount You Get */}
          <div className="flex justify-between items-center py-3 border-b">
            <div>
              <span className="text-base font-semibold text-gray-900">Amount You Get</span>
              <p className="text-xs text-gray-500">Loan Amount - Deduction</p>
            </div>
            <span className="text-lg !font-extrabold text-intellend-green" style={{ fontWeight: 900 }}>
              {formatCurrency(offerData.net_disbursement)}
            </span>
          </div>

          {/* Amount You Pay */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-base font-semibold text-gray-900">Amount You Pay</span>
            <span className="text-lg font-bold text-gray-900">
              {formatCurrency(offerData.total_repayment)}
            </span>
          </div>

          {/* Total Interest Payable */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-base font-semibold text-gray-900">Total Interest Payable</span>
            <span className="text-lg font-bold text-gray-900">
              {formatCurrency(offerData.total_interest)}
            </span>
          </div>

          {/* Daily Installment */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-base font-semibold text-gray-900">Daily Installment</span>
            <span className="text-lg font-bold text-intellend-green">
              {formatCurrency(offerData.daily_installment)}
            </span>
          </div>

          {/* Loan Tenure */}
          <div className="flex justify-between items-center py-3">
            <span className="text-base font-semibold text-gray-900">Loan Tenure</span>
            <span className="text-lg font-bold text-gray-900">
              {offerData.tenure}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Info Message */}
      <Alert>
        <AlertDescription className="text-sm text-center">
          Please review your final offer details carefully before proceeding to the next step.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default CaprionFinalOffer;
